export const GLOSSARY = {
  // ── Volatility / Indices ──────────────────────────────────────────────
  'VIX': {
    def: 'CBOE Volatility Index — measures the market\'s expected 30-day volatility derived from S&P 500 options prices. Known as the "fear gauge."',
    why: 'Above 20 = elevated fear. Below 15 = complacency. Sudden spikes often mark short-term market bottoms.',
  },
  'S&P 500': {
    def: 'Market-cap-weighted index of 500 large US public companies. The primary benchmark for US equities.',
    why: 'The most-watched equity gauge globally; institutional allocations treat it as the baseline risk asset.',
  },
  'Nasdaq': {
    def: 'Tech-heavy US equity index. Overweights technology, semiconductors, and high-growth companies.',
    why: 'Outperforms in risk-on regimes; underperforms when rates rise or liquidity tightens.',
  },
  'DXY': {
    def: 'US Dollar Index — measures the dollar against a basket of 6 major currencies: EUR, JPY, GBP, CAD, SEK, CHF.',
    why: 'A stronger DXY pressures commodities (oil, gold) and US multinationals\' overseas earnings.',
  },
  'Mega-Cap Tech': {
    def: 'The handful of US technology companies with $1 trillion+ market caps — Apple, Microsoft, Nvidia, Alphabet, Amazon, Meta.',
    why: 'Their combined weight in the S&P 500 and Nasdaq is so large that their performance effectively drives the index.',
  },

  // ── Credit / Spreads ──────────────────────────────────────────────────
  'IG OAS': {
    def: 'Investment Grade Option-Adjusted Spread — the extra yield investors demand to hold investment-grade corporate bonds over risk-free Treasuries, in basis points.',
    why: 'A rising IG OAS signals credit stress even among the safest corporates. Sub-100bp = tight/healthy; above 150bp = stress.',
  },
  'HY OAS': {
    def: 'High Yield Option-Adjusted Spread — the extra yield on junk bonds over Treasuries, in basis points. A key gauge of risk appetite in credit markets.',
    why: 'HY OAS above 500bp typically signals recession fears. Sub-300bp = risk-on, tight credit conditions.',
  },
  'OAS': {
    def: 'Option-Adjusted Spread — the yield spread of a bond over the risk-free rate, adjusted for any embedded options (like call provisions). Measured in basis points.',
    why: 'The cleanest measure of credit risk premium. Tighter OAS = investors comfortable with risk; wider = risk aversion.',
  },
  'credit spreads': {
    def: 'The yield premium that corporate bonds pay over equivalent-maturity government bonds, compensating investors for default risk.',
    why: 'Tightening credit spreads = market pricing in low default risk (risk-on). Widening = stress, tightening financial conditions.',
  },
  'equity risk premium': {
    def: 'The excess return that investors demand for holding equities over a risk-free asset (like Treasuries). ERP = expected equity return minus risk-free rate.',
    why: 'A compressed ERP means stocks are expensive relative to bonds — a warning sign when rates are elevated.',
  },
  'C&I loans': {
    def: 'Commercial & Industrial loans — bank lending to businesses for operations, inventory, payroll, and capital expenditure.',
    why: 'C&I loan growth is a leading indicator of business confidence. A contraction signals banks tightening standards or businesses pulling back.',
  },
  'IG': {
    def: 'Investment Grade — bonds rated BBB-/Baa3 or above by S&P/Moody\'s. Considered low default risk.',
    why: 'IG credit is the benchmark for corporate borrowing costs. When IG spreads widen, all corporate borrowing gets more expensive.',
  },
  'HY': {
    def: 'High Yield — bonds rated BB+/Ba1 or below. Also called "junk bonds." Higher default risk, higher yield.',
    why: 'HY performance is a leading indicator of risk appetite — it rallies early in risk-on and sells off early in risk-off.',
  },
  'spread': {
    def: 'The yield difference between two bonds or rates — typically a corporate bond vs a Treasury of the same maturity.',
    why: 'Widening spreads signal rising credit stress; tightening spreads signal improving risk appetite.',
  },
  'CLO': {
    def: 'Collateralized Loan Obligation — a structured product that pools leveraged loans and tranches them by risk/return. A major buyer of corporate debt.',
    why: 'CLO demand drives conditions in the leveraged loan market; stress in CLOs signals tightening credit for mid-market companies.',
  },
  'CDS': {
    def: 'Credit Default Swap — a financial contract that pays out if a borrower defaults. Functions like insurance on debt.',
    why: 'Rising CDS premiums on a company or country signal the market is pricing in higher default risk.',
  },

  // ── Rates / Macro ─────────────────────────────────────────────────────
  'FRED': {
    def: 'Federal Reserve Economic Data — a free database of 800,000+ economic time series maintained by the St. Louis Fed.',
    why: 'The authoritative source for macro indicators: GDP, CPI, unemployment, and yield data used in thesis signals.',
  },
  'Fed': {
    def: 'The Federal Reserve — the US central bank. Controls monetary policy, sets interest rates, and supervises banks.',
    why: 'Fed policy is the dominant macro force: rate cuts are broadly bullish, rate hikes are broadly bearish.',
  },
  'FOMC': {
    def: 'Federal Open Market Committee — the Fed body that votes on the federal funds rate (the benchmark interest rate).',
    why: 'FOMC decisions are the single biggest short-term driver of asset prices globally.',
  },
  'CPI': {
    def: 'Consumer Price Index — measures the average change in prices paid by consumers for goods and services.',
    why: 'The Fed\'s dual mandate includes price stability; CPI above the 2% target influences rate decisions.',
  },
  'PCE': {
    def: 'Personal Consumption Expenditures price index — the Fed\'s preferred inflation measure, broader than CPI.',
    why: 'Core PCE (ex food & energy) is what the Fed watches most closely when setting interest rates.',
  },
  'GDP': {
    def: 'Gross Domestic Product — the total value of goods and services produced in a country over a given period.',
    why: 'Two consecutive quarters of negative GDP = recession. GDP trajectory drives earnings and risk appetite.',
  },
  'yield curve': {
    def: 'A chart plotting bond yields across maturities. Normal = upward slope. Inverted (short > long) historically precedes recessions.',
    why: 'The 2yr vs 10yr spread is the most-watched recession predictor in macro analysis.',
  },
  'basis points': {
    def: '1 basis point (bp) = 0.01 percentage point. Standard unit for measuring changes in rates, yields, and spreads.',
    why: '"25bp cut" means rates fall by 0.25%. Eliminates ambiguity when discussing small rate moves.',
  },
  'bps': {
    def: 'Basis points — 1 bp = 0.01 percentage point. Standard unit for interest rates, yield spreads, and fees.',
    why: 'When you see "IG OAS at 86bps," that means investment-grade bonds yield 0.86% more than equivalent Treasuries.',
  },
  'bp': {
    def: 'Basis point — 0.01 percentage point. Used to measure small changes in rates, spreads, and yields.',
    why: 'A "25bp rate cut" = the Fed lowered its benchmark rate by 0.25 percentage points.',
  },
  'Treasury': {
    def: 'US government bonds issued by the Treasury Department. The global risk-free benchmark for borrowing costs.',
    why: '10-year Treasury yield sets the "floor" for all other interest rates and discount rates globally.',
  },
  'SOFR': {
    def: 'Secured Overnight Financing Rate — the overnight rate for borrowing cash collateralized by Treasuries. Replaced LIBOR in 2023.',
    why: 'SOFR is now the benchmark for trillions in floating-rate loans and derivatives.',
  },
  'repo': {
    def: 'Repurchase agreement — a short-term borrowing arrangement where one party sells securities and agrees to buy them back the next day at a slightly higher price.',
    why: 'Repo markets are the plumbing of the financial system. Stress in repo (spiking rates) signals liquidity problems.',
  },
  'QT': {
    def: 'Quantitative Tightening — when the Fed shrinks its balance sheet by letting bonds mature without reinvesting, removing liquidity from the system.',
    why: 'QT is a headwind for asset prices: it drains reserves and pushes up long-term yields.',
  },
  'QE': {
    def: 'Quantitative Easing — when the Fed buys bonds to inject money into the financial system and suppress long-term yields.',
    why: 'QE inflates asset prices by flooding the system with liquidity; its reversal (QT) does the opposite.',
  },
  'carry': {
    def: 'The yield or income earned from holding an asset, net of financing cost. "Positive carry" means the asset pays more than it costs to hold.',
    why: 'In a carry trade, investors borrow in low-rate currencies and invest in high-rate ones. When carry unwinds, it can cause sharp moves.',
  },
  'stagflation': {
    def: 'The toxic combination of high inflation and stagnant or negative economic growth — the worst of both worlds for policymakers.',
    why: 'Stagflation ties the Fed\'s hands: raising rates fights inflation but worsens growth; cutting rates does the opposite.',
  },
  'soft landing': {
    def: 'When a central bank slows the economy enough to reduce inflation without causing a recession.',
    why: 'A rare outcome; markets rally in anticipation of a soft landing and sell off if the data suggests it\'s failing.',
  },
  'hard landing': {
    def: 'When central bank rate hikes slow the economy too sharply, tipping it into recession.',
    why: 'Hard landing fears drive risk-off moves: equities sell off, credit spreads widen, safe havens rally.',
  },
  'recession': {
    def: 'Technically defined as two consecutive quarters of negative GDP growth. Broadly: a significant decline in economic activity.',
    why: 'Recessions compress earnings, widen credit spreads, and force the Fed to cut rates.',
  },
  'drawdown': {
    def: 'The peak-to-trough percentage decline in an asset\'s value from its most recent high.',
    why: 'A 20%+ drawdown from a peak = bear market. Drawdown magnitude signals how much risk is being repriced.',
  },
  'risk-on': {
    def: 'Market sentiment favoring riskier assets — equities, commodities, high-yield bonds — over safe havens.',
    why: 'Driven by strong growth expectations, low volatility, and accommodative central banks.',
  },
  'risk-off': {
    def: 'Market sentiment fleeing to safe assets — Treasuries, gold, USD, JPY — away from equities and credit.',
    why: 'Triggered by geopolitical shocks, recession fears, or credit stress events.',
  },

  // ── Commodities / Energy ─────────────────────────────────────────────
  'Brent': {
    def: 'Brent Crude — the international oil benchmark sourced from the North Sea. Priced in USD per barrel.',
    why: 'Sets the global oil price. Rising Brent feeds into inflation and signals geopolitical stress.',
  },
  'WTI': {
    def: 'West Texas Intermediate — the US domestic crude oil benchmark. Typically trades $1–3 below Brent.',
    why: 'Key input for US energy stocks and inflation readings; divergence from Brent signals domestic supply shifts.',
  },
  'crude': {
    def: 'Crude oil — raw, unrefined petroleum extracted from the ground. The feedstock for gasoline, diesel, jet fuel, and petrochemicals.',
    why: 'Crude price is a direct input to inflation, energy sector earnings, and geopolitical leverage for oil-producing nations.',
  },
  'OPEC': {
    def: 'Organization of the Petroleum Exporting Countries — a cartel of 13 oil-producing nations that coordinates production quotas to influence global oil prices.',
    why: 'OPEC production cuts prop up oil prices; increases suppress them. OPEC+ includes Russia and allies, amplifying the effect.',
  },
  'UAE': {
    def: 'United Arab Emirates — a major OPEC+ member and one of the world\'s largest oil exporters (~3.5 million barrels/day).',
    why: 'UAE\'s production decisions and alignment within OPEC+ directly affect global oil supply and price.',
  },
  'IRGC': {
    def: 'Islamic Revolutionary Guard Corps — Iran\'s elite military and paramilitary organization. Designated a foreign terrorist organization by the US. Controls significant portions of Iran\'s oil sector and regional proxy network.',
    why: 'IRGC activity in the Middle East (Strait of Hormuz, Yemen, Iraq) is a key driver of energy supply risk and geopolitical risk premiums.',
  },

  // ── Options / Trading Strategies ─────────────────────────────────────
  'defined-risk puts': {
    def: 'A put options strategy with a capped maximum loss — typically a put spread (buy one put, sell a further out-of-the-money put). You pay a net premium; that\'s your max loss.',
    why: 'Allows traders to express a bearish view or hedge downside without unlimited risk, unlike naked short positions.',
  },
  'naked shorts': {
    def: 'Short selling shares without first borrowing them — effectively selling shares that don\'t yet exist. Banned or heavily restricted in most regulated markets.',
    why: 'Can amplify selling pressure on a stock. When forced to cover, "short squeezes" can cause violent rallies.',
  },

  // ── Equities / Valuation ──────────────────────────────────────────────
  'P/E': {
    def: 'Price-to-Earnings ratio — stock price divided by earnings per share. The most common equity valuation metric.',
    why: 'High P/E = market expects strong growth (or rates are low). P/E compression destroys equity value even if earnings hold.',
  },
  'EPS': {
    def: 'Earnings Per Share — a company\'s net profit divided by its shares outstanding.',
    why: 'EPS growth drives stock prices over time. Revisions to EPS estimates are a primary catalyst for stock moves.',
  },
  'ETF': {
    def: 'Exchange-Traded Fund — a basket of securities (stocks, bonds, commodities) trading on an exchange like a single stock.',
    why: 'ETF flows reveal institutional positioning; large inflows/outflows signal conviction directional bets.',
  },
  'BDC': {
    def: 'Business Development Company — a publicly-traded firm that lends to or invests in small/mid-sized businesses, paying high dividends.',
    why: 'BDCs proxy private credit conditions; stressed BDCs signal tightening in leveraged lending markets.',
  },

  // ── Specific Tickers ─────────────────────────────────────────────────
  'TLT': {
    def: 'iShares 20+ Year Treasury Bond ETF (NASDAQ: TLT) — tracks long-dated US government bonds. The primary liquid instrument for expressing views on long-term interest rates.',
    why: 'TLT falls when long-term rates rise and rallies when they fall. It\'s the go-to hedge or trade for rate duration exposure.',
  },
  'OWL': {
    def: 'Blue Owl Capital (NYSE: OWL) — a large alternative asset manager specializing in direct lending, GP capital solutions, and net lease real estate. One of the largest non-bank lenders to mid-market companies.',
    why: 'OWL\'s performance and deal flow are a leading indicator of private credit appetite and institutional demand for yield in the direct lending space.',
  },
  'ARCC': {
    def: 'Ares Capital Corporation — the world\'s largest Business Development Company (BDC), with $21B+ in assets. Lends to mid-market US companies.',
    why: 'ARCC is the bellwether for private credit health. Its spread performance and dividend sustainability signal conditions in middle-market lending.',
  },
  'MSFT': {
    def: 'Microsoft Corporation — $3T+ market cap technology conglomerate. Businesses: Azure (cloud), Office 365, LinkedIn, Xbox, and a major stake in OpenAI.',
    why: 'MSFT is the second-largest S&P 500 component; its earnings and Azure growth rate are bellwethers for enterprise AI adoption and cloud spend.',
  },
  'HPK': {
    def: 'HighPeak Energy — a Permian Basin oil & gas exploration & production (E&P) company focused on tight oil in the Midland Basin.',
    why: 'A pure-play US onshore oil producer; HPK\'s performance tracks WTI prices and Permian Basin drilling economics closely.',
  },
  'ADMA': {
    def: 'ADMA Biologics — a specialty biopharmaceutical company focused on plasma-derived biologics for immune deficiency disorders and infectious disease.',
    why: 'A small-cap healthcare name; its performance reflects biotech risk appetite and niche demand for specialty plasma products.',
  },

  // ── Prediction Markets ────────────────────────────────────────────────
  'Polymarket': {
    def: 'A blockchain-based prediction market where traders bet real money on event outcomes, creating crowd-sourced probability estimates.',
    why: 'Polymarket probabilities often lead traditional indicators — money on the line aggregates information efficiently.',
  },
};

// Build a single regex matching all terms (longest first to avoid partial matches)
// Word boundaries prevent mid-word matches (e.g. "IG" inside "sign")
const sorted = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length);

const escaped = sorted.map(term => {
  const e = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const startBound = /^\w/.test(term) ? '\\b' : '';
  const endBound = /\w$/.test(term) ? '\\b' : '';
  return `${startBound}${e}${endBound}`;
});

export const GLOSSARY_REGEX = new RegExp(
  `(${escaped.join('|')})`,
  'gi'
);
